import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@component/button";
import { PlusIcon, SearchIcon } from "lucide-react";
import { Input } from "@component/input";
import { DemoInterviewItem } from "@type/interview";
import { DemoTable } from "@container/demo-interview-table";
import { getDemoCompanyFilter } from "@service/assessment";
import { defaultPagination } from "@constant/app";
import { MetaPagination } from "@type/common";
import AuthLayout from "@container/layout/auth";
import Pagination from "@component/pagination";

export default function DemoInterviewPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(defaultPagination.page);
    const [keyword, setKeyword] = useState<string>("");
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })

    // Manage the master data state
    const [demoInterviews, setDemoInterviews] = useState<DemoInterviewItem[]>([]);

    const getListDemoInterviews = async (page: number, keyword?: string) => {
        setIsLoading(true);
        getDemoCompanyFilter({
            page,
            limit: defaultPagination.limit,
            keyword
        }).then((resp) => {
            if (resp.status === 200) {
                setDemoInterviews(resp.data.data);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }

    // Function to update individual entries
    const updateEntry = (id: string, updates: Partial<DemoInterviewItem>) => {
        setDemoInterviews((prevData) =>
            prevData.map((entry) =>
                entry.id === id ? { ...entry, ...updates } : entry
            )
        );
    };

    const onLoadPagination = (page: number) => {
        setCurrentPage(page);
        getListDemoInterviews(page, "")
    }

    const onKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value);
        if (e.target.value === "") {
            getListDemoInterviews(1, "")
        }
    }

    const onSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        getListDemoInterviews(1, keyword);
    }

    // Load saved filters from localStorage on mount
    useEffect(() => {
        getListDemoInterviews(1, "")
    }, []);

    return (
        <AuthLayout>
            <div
                className="space-y-6"
                style={{
                    paddingTop: "16px",
                    paddingRight: "16px",
                    paddingBottom: "16px",
                    paddingLeft: "16px",
                }}
            >
                <div className="space-y-6">
                    {/* Main Header */}
                    <div className="flex items-center justify-start">
                        <Link to="/demo/create">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Create Company Demo
                            </Button>
                        </Link>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex items-center justify-between gap-4 w-full">
                        <div className="flex-1 relative">
                            <form onSubmit={onSearch}>

                                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

                                <Input placeholder="Search companies..." className="pl-10" onChange={onKeywordChange} />
                                <button type="submit" className="hidden">Search</button>
                            </form>
                        </div>
                    </div>
                </div>

                <DemoTable data={demoInterviews} onUpdateEntry={updateEntry} />

                <div className="p-4">
                    <Pagination
                        isLoading={isLoading}
                        currentPage={currentPage}
                        totalPages={metaPagination.total_pages || 0}
                        onPageChange={onLoadPagination}
                    />
                </div>
            </div >
        </AuthLayout >
    );
}
